import type { Locale } from "@/catalog";
import type { OpportunityCurrency } from "@/pricing";

export function formatInteger(value: number, locale: Locale): string {
  return new Intl.NumberFormat(locale === "pt" ? "pt-BR" : "en-US").format(value);
}

export function formatMoney(
  cents: number,
  locale: Locale,
  currency: OpportunityCurrency,
): string {
  return new Intl.NumberFormat(locale === "pt" ? "pt-BR" : "en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

export function companyPath(locale: Locale, slug: string): string {
  return `/${locale}/company/${slug}`;
}

export async function copyToClipboard(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return;
    } catch {
      // Sandboxed and privacy-hardened browsers may deny the async clipboard API.
    }
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) throw new Error("clipboard_unavailable");
}

export function outcomeToken(value: string, locale: Locale): string {
  const labels: Record<string, { en: string; pt: string }> = {
    shutdown: { en: "Shut down", pt: "Encerrada" },
    inactive: { en: "Inactive", pt: "Inativa" },
    acquired: { en: "Acquired", pt: "Adquirida" },
    acquired_then_product_shutdown: { en: "Acquired; product shut down", pt: "Adquirida; produto encerrado" },
    shutdown_then_acquired_and_relaunched: { en: "Shut down, acquired, and relaunched", pt: "Encerrada, adquirida e relançada" },
    acquired_and_integrated: { en: "Acquired and integrated", pt: "Adquirida e integrada" },
    operating_after_yc_separation: { en: "Operating after YC separation", pt: "Operando após separação da YC" },
    public: { en: "Public company", pt: "Empresa de capital aberto" },
    pivoted: { en: "Pivoted", pt: "Mudou de direção" },
    unknown: { en: "Not established", pt: "Não estabelecido" },
    confirmed: { en: "Confirmed", pt: "Confirmado" },
    reported: { en: "Reported", pt: "Reportado" },
    inferred: { en: "Inference", pt: "Inferência" },
    not_publicly_established: { en: "Not publicly established", pt: "Não estabelecida publicamente" },
    not_applicable: { en: "Not a shutdown", pt: "Não houve encerramento" },
    high: { en: "high", pt: "alta" },
    medium: { en: "medium", pt: "média" },
    low: { en: "low", pt: "baixa" },
  };
  return labels[value]?.[locale] ?? value.replaceAll("_", " ");
}

export function verdictToken(value: "build" | "adapt" | "do_not_build_yet", locale: Locale): string {
  const labels = {
    build: { en: "Build", pt: "Construir" },
    adapt: { en: "Adapt", pt: "Adaptar" },
    do_not_build_yet: { en: "Do not build yet", pt: "Não construir ainda" },
  } as const;
  return labels[value][locale];
}

export function sourceStatusToken(value: string | undefined, locale: Locale): string {
  if (!value) return "—";
  const normalized = value.trim().toLowerCase();
  if (locale === "pt") {
    if (normalized === "inactive") return "inativa";
    if (normalized === "acquired") return "adquirida";
    if (normalized === "public") return "aberta";
  }
  return value;
}

export function firstSentence(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return "";
  const match = trimmed.match(/^.+?[.!?…](?=\s|$)/u);
  return (match?.[0] ?? trimmed).trim();
}
